import { inject, Injectable, signal, computed, InjectionToken, Optional } from '@angular/core';
import { ZOP_BRANDING_CONFIG, ZopBrandingConfig } from './branding-config';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

// Define a minimal interface for layout config to avoid circular dependency
export interface LayoutConfig {
  scheme?: 'auto' | 'dark' | 'light';
}

// Injection token for layout config service - to be provided by zop-ux-layout
export const ZOP_LAYOUT_CONFIG_SERVICE = new InjectionToken<{ config$: Observable<LayoutConfig> }>(
  'ZOP_LAYOUT_CONFIG_SERVICE'
);

/**
 * Branding service
 * Provides app-specific branding assets and theme-aware logo selection
 */
@Injectable({ providedIn: 'root' })
export class ZopBrandingService {
  private readonly _config = inject(ZOP_BRANDING_CONFIG);
  
  // Optionally inject layout config to avoid circular dependency
  private readonly _layoutConfig = inject(ZOP_LAYOUT_CONFIG_SERVICE, { optional: true });

  // Convert layout config observable to signal
  private readonly _layoutConfigSignal = this._layoutConfig
    ? toSignal(this._layoutConfig.config$)
    : signal<LayoutConfig | undefined>(undefined);

  // Track system dark mode preference
  private readonly _systemDarkMode = signal<boolean>(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  /**
   * Constructor
   * Sets up media query listener for system theme changes
   */
  constructor() {
    // Listen to system theme changes (only in browser)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

      // Modern browsers
      if (darkModeQuery.addEventListener) {
        darkModeQuery.addEventListener('change', (e) => {
          this._systemDarkMode.set(e.matches);
        });
      }
      // Legacy browsers
      else if (darkModeQuery.addListener) {
        darkModeQuery.addListener((e: MediaQueryListEvent) => {
          this._systemDarkMode.set(e.matches);
        });
      }
    }
  }

  /**
   * Computed signal that determines if dark theme is active
   * Takes into account: scheme setting + system preference (when scheme is 'auto')
   */
  readonly isDarkTheme = computed(() => {
    const scheme = this._layoutConfigSignal()?.scheme ?? 'light';

    if (scheme === 'dark') {
      return true;
    } else if (scheme === 'light') {
      return false;
    } else {
      // 'auto' - use system preference
      return this._systemDarkMode();
    }
  });

  /**
   * Current default logo URL based on active theme
   * Automatically switches between light and dark logo for proper contrast
   * IMPORTANT: Dark theme shows LIGHT logo, light theme shows DARK logo
   * Use this when you don't need a specific variant (full/square)
   */
  readonly currentLogoUrl = computed(() =>
    this.isDarkTheme() ? this._config.logoLightUrl : this._config.logoDarkUrl
  );

  /**
   * Current full logo URL (with app name/text) based on active theme
   * Falls back to default logo if full variant is not configured
   * IMPORTANT: Dark theme shows LIGHT logo, light theme shows DARK logo (for contrast)
   */
  readonly currentFullLogoUrl = computed(() => {
    const isDark = this.isDarkTheme();
    // Dark theme needs light logo for contrast
    if (isDark && this._config.logoFullLightUrl) {
      return this._config.logoFullLightUrl;
    }
    // Light theme needs dark logo for contrast
    if (!isDark && this._config.logoFullDarkUrl) {
      return this._config.logoFullDarkUrl;
    }
    // Fallback to default logo
    return this.currentLogoUrl();
  });

  /**
   * Current square/compact logo URL (icon only) based on active theme
   * Falls back to default logo if square variant is not configured
   * IMPORTANT: Dark theme shows LIGHT logo, light theme shows DARK logo (for contrast)
   */
  readonly currentSquareLogoUrl = computed(() => {
    const isDark = this.isDarkTheme();
    // Dark theme needs light logo for contrast
    if (isDark && this._config.logoSquareLightUrl) {
      return this._config.logoSquareLightUrl;
    }
    // Light theme needs dark logo for contrast
    if (!isDark && this._config.logoSquareDarkUrl) {
      return this._config.logoSquareDarkUrl;
    }
    // Fallback to default logo
    return this.currentLogoUrl();
  });

  /**
   * Application name
   */
  get appName(): string {
    return this._config.appName;
  }

  /**
   * Logo alt text for accessibility
   */
  get logoAlt(): string {
    return this._config.logoAlt;
  }

  /**
   * Logo URL for light theme
   */
  get logoLightUrl(): string {
    return this._config.logoLightUrl;
  }

  /**
   * Logo URL for dark theme
   */
  get logoDarkUrl(): string {
    return this._config.logoDarkUrl;
  }

  /**
   * Favicon URL (optional)
   */
  get faviconUrl(): string | undefined {
    return this._config.faviconUrl;
  }

  /**
   * Get a named image URL
   * @param key Image key from config.images
   * @returns Image URL or undefined if not found
   *
   * @example
   * ```typescript
   * const heroUrl = brandingService.getImage('authHero');
   * ```
   */
  getImage(key: string): string | undefined {
    return this._config.images?.[key];
  }

  /**
   * Get a named animation URL
   * @param key Animation key from config.animations
   * @returns Animation URL or undefined if not found
   *
   * @example
   * ```typescript
   * const animUrl = brandingService.getAnimation('authHero');
   * ```
   */
  getAnimation(key: string): string | undefined {
    return this._config.animations?.[key];
  }

  /**
   * Get all configured images
   */
  get images(): Record<string, string> {
    return this._config.images ?? {};
  }

  /**
   * Get all configured animations
   */
  get animations(): Record<string, string> {
    return this._config.animations ?? {};
  }

  /**
   * Get the complete branding configuration
   */
  get config(): ZopBrandingConfig {
    return this._config;
  }

  /**
   * Current sidebar background color based on active theme
   * Falls back to undefined if not configured (will use default Tailwind colors)
   */
  readonly currentSidebarBgColor = computed(() => {
    const themeColors = this._config.themeColors;
    if (!themeColors?.sidebarBgColor) {
      return undefined;
    }

    const isDark = this.isDarkTheme();
    return isDark && themeColors.sidebarBgColorDark
      ? themeColors.sidebarBgColorDark
      : themeColors.sidebarBgColor;
  });

  /**
   * Current sidebar text color based on active theme
   * Falls back to undefined if not configured (will use default Tailwind colors)
   */
  readonly currentSidebarTextColor = computed(() => {
    const themeColors = this._config.themeColors;
    if (!themeColors?.sidebarTextColor) {
      return undefined;
    }

    const isDark = this.isDarkTheme();
    return isDark && themeColors.sidebarTextColorDark
      ? themeColors.sidebarTextColorDark
      : themeColors.sidebarTextColor;
  });

  /**
   * Current sidebar background gradient based on active theme
   * Takes precedence over sidebarBgColor when provided
   * Falls back to undefined if not configured
   */
  readonly currentSidebarBgGradient = computed(() => {
    const themeColors = this._config.themeColors;
    if (!themeColors?.sidebarBgGradient) {
      return undefined;
    }
    const isDark = this.isDarkTheme();
    return isDark && themeColors.sidebarBgGradientDark
      ? themeColors.sidebarBgGradientDark
      : themeColors.sidebarBgGradient;
  });

  /**
   * Current sidebar navigation color based on active theme
   * Returns the appropriate color for light or dark mode
   * Falls back to undefined if not configured (will use default Tailwind colors)
   */
  readonly currentSidebarNavColor = computed(() => {
    const themeColors = this._config.themeColors;
    if (!themeColors) {
      return undefined;
    }

    const isDark = this.isDarkTheme();
    // Use dark mode color if provided, otherwise fall back to light mode color
    return isDark && themeColors.sidebarNavColorDark
      ? themeColors.sidebarNavColorDark
      : themeColors.sidebarNavColor;
  });

  /**
   * Determines if a color is considered dark (luminance < 0.5)
   * Used to decide if light or dark logo should be used
   * @private
   */
  private _isColorDark(color: string | undefined): boolean {
    if (!color) {
      // Default sidebar: light mode = white (light), dark mode = gray-900 (dark)
      return this.isDarkTheme();
    }

    // Convert hex to RGB
    let r = 0,
      g = 0,
      b = 0;

    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    } else if (color.startsWith('rgb')) {
      // Handle rgb/rgba colors
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        r = parseInt(match[0]);
        g = parseInt(match[1]);
        b = parseInt(match[2]);
      }
    }

    // Calculate relative luminance
    // Formula: (0.299 * R + 0.587 * G + 0.114 * B) / 255
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // If luminance is less than 0.5, color is considered dark
    return luminance < 0.5;
  }

  /**
   * Determines if the sidebar background (color or gradient) is visually dark
   * for the purpose of logo contrast selection
   * @private
   */
  private _isSidebarDark(): boolean {
    const themeColors = this._config.themeColors;
    // Gradient takes precedence — use the explicit hint, defaulting to true
    if (themeColors?.sidebarBgGradient) {
      return themeColors.sidebarBgGradientIsDark !== false;
    }
    return this._isColorDark(this.currentSidebarBgColor());
  }

  /**
   * Logo URL for sidebar based on sidebar background color brightness
   * Dark sidebar → light logo, Light sidebar → dark logo
   * This ensures proper contrast regardless of theme
   */
  readonly currentSidebarLogoUrl = computed(() => {
    const isSidebarDark = this._isSidebarDark();

    // Dark sidebar needs light logo for contrast
    return isSidebarDark ? this._config.logoLightUrl : this._config.logoDarkUrl;
  });

  /**
   * Full logo URL for sidebar based on sidebar background color brightness
   * Dark sidebar → light logo, Light sidebar → dark logo
   */
  readonly currentSidebarFullLogoUrl = computed(() => {
    const isSidebarDark = this._isSidebarDark();

    // Dark sidebar needs light logo for contrast
    if (isSidebarDark && this._config.logoFullLightUrl) {
      return this._config.logoFullLightUrl;
    }
    if (!isSidebarDark && this._config.logoFullDarkUrl) {
      return this._config.logoFullDarkUrl;
    }

    // Fallback to default sidebar logo
    return this.currentSidebarLogoUrl();
  });

  /**
   * Square logo URL for sidebar based on sidebar background color brightness
   * Dark sidebar → light logo, Light sidebar → dark logo
   */
  readonly currentSidebarSquareLogoUrl = computed(() => {
    const isSidebarDark = this._isSidebarDark();

    // Dark sidebar needs light logo for contrast
    if (isSidebarDark && this._config.logoSquareLightUrl) {
      return this._config.logoSquareLightUrl;
    }
    if (!isSidebarDark && this._config.logoSquareDarkUrl) {
      return this._config.logoSquareDarkUrl;
    }

    // Fallback to default sidebar logo
    return this.currentSidebarLogoUrl();
  });

  /**
   * Check if sidebar colors are configured
   */
  readonly hasSidebarColors = computed(() => {
    return !!this._config.themeColors?.sidebarBgColor ||
           !!this._config.themeColors?.sidebarBgGradient;
  });
}
